import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { CountryCode, SubscriptionPlan, SubscriptionPlanCode, Variant } from '@dayetopia/types';
import { useLocation } from '@reach/router';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { Context } from '@components/Context';
import useFetch from '@components/useFetch';
import { DIAGNOSTIC_TAMPON_PRODUCT_CODES } from '@constants';
import {
  ProductShoppingContextProps,
  BoxState,
  PadsBoxState,
  Prices,
  ProductCode,
  default12TamponConfiguration,
  defaultPadsConfiguration,
  HeaderData,
  StickyBannerData,
  ProductOption,
  ProductAddon,
  CartItem,
  PadsBoxStackState,
  defaultPadsStackConfiguration
} from '@contracts';
import { getLinerPadsPrice, getPadsPrice, getRegularPadsPrice, getSuperPadsPrice, getSuperPlusPadsPrice, getBoxPrice } from '@utils/boxPrices';
import { combineCartItemsAndAddOns, divideCartItems } from '@utils/cart';
import { handleFetchProductPriceAndSubscriptionPlans } from '@utils/getSubscriptionPlans';
import { replaceText } from '@utils/handleTextReplacement';
import { getProductPrice } from '@utils/price';
import { convertBoxStateToBoxItems } from '@utils/product';
import { productPageTrack } from '@utils/tracking';
import orderItems from '@utils/useOrderedItems';

const defaultSettings: ProductShoppingContextProps = {
  selectedBox: undefined,
  productHeaderData: {} as HeaderData,
  stickyBanner: undefined,
  filteredLinks: [],
  hasFeedbackView: false,
  topSubtitleText: '',
  paymentOptionsHeader: '',
  headingText: '',
  bodyText: '',
  subtextText: '',
  orderedSlides: [],
  loaded: false,
  price: 0,
  boxState: undefined,
  padsBoxState: undefined,
  showQuantity: false,
  subscriptionId: '',
  configVariant: '',
  layout: '',
  subscriptions: [],
  padsBoxStackState: undefined,
  isTracked: false,
  prices: {},
  paymentOptions: [],
  paymentOptionSelected: null,
  isUsingNewDesignSystem: false,
  isDiagnosticTamponProduct: false,
  showNurseConsultation: false,
  preselectedTamponBoxes: false,
  productId: '',
  scrollHeight: undefined,
  hasProductOptions: false,
  productOptionsInfo: [],
  productAddonsInfo: [],
  selectedOptionCartItem: null,
  selectedAddonCartItems: [],
  selectedCartItems: [],
  setLoaded: () => {},
  setPrice: () => {},
  setBoxState: () => {},
  setPadsBoxState: () => {},
  setShowQuantity: () => {},
  setSubscriptionId: () => {},
  setConfigVariant: () => {},
  setLayout: () => {},
  setSubscriptions: () => {},
  setIsTracked: () => {},
  setPadsBoxStackState: () => {},
  setPrices: () => {},
  setPaymentOptionSelected: () => {},
  setProductId: () => {},
  setSelectedOptionCartItem: () => {},
  setSelectedAddonCartItems: () => {},
  setScrollHeight: () => {}
};

export const ShoppingContext = createContext<ProductShoppingContextProps>(defaultSettings);

export const useShoppingContext = () => useContext(ShoppingContext);

interface Props extends React.PropsWithChildren {
  productHeaderData: HeaderData;
  stickyBanner?: StickyBannerData;
  preselectedTamponBoxes?: boolean;
}

export const ProductShoppingProvider = ({ children, productHeaderData, preselectedTamponBoxes = true }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { state } = useContext(Context);
  const { currency, country, firebaseAuth, selectedBox } = state;
  const { images, videos, links, feedback, topSubtitle, text, subtext, heading, payment_options_header, productCode, is_using_new_design_system } =
    productHeaderData;
  const orderedSlides = useMemo(() => {
    const filteredImages = images?.filter((item) => item.product_image) || [];
    const filteredVideos = videos?.filter((item) => item.video?.url) || [];
    const itemsToOrder = [...filteredVideos, ...filteredImages];

    return orderItems(itemsToOrder);
  }, [images, videos]);
  const filteredLinks = useMemo(() => links?.filter((item) => item?.link?.text !== '' && item?.text?.text !== '') || [], [links]);
  const [loaded, setLoaded] = useState(true);
  const [selectedOptionCartItem, setSelectedOptionCartItem] = useState<CartItem | null>(null);
  const [selectedAddonCartItems, setSelectedAddonCartItems] = useState<CartItem[]>([]);

  const getSelectedCartItems = async () => {
    if (selectedOptionCartItem) {
      const { combinedCartItem, remainingAddOnItems } = await combineCartItemsAndAddOns(
        selectedOptionCartItem,
        selectedAddonCartItems,
        currency,
        country
      );

      let allCartItems: CartItem[] = [];

      if (combinedCartItem) {
        const devidedCartItems = divideCartItems(combinedCartItem, country);
        allCartItems = [...devidedCartItems];
      }

      if (remainingAddOnItems) {
        allCartItems = [...allCartItems, ...remainingAddOnItems];
      }

      return allCartItems;
    }
    return [...selectedAddonCartItems];
  };

  const [selectedCartItems, setSelectedCartItems] = useState<CartItem[]>([]);

  useEffect(() => {
    const fetchSelectedCartItems = async () => {
      const items = await getSelectedCartItems();
      setSelectedCartItems(items);
    };

    fetchSelectedCartItems();
  }, [selectedOptionCartItem, selectedAddonCartItems]);

  const [price, setPrice] = useState<number>(-1);
  const [boxState, setBoxState] = useState<BoxState>(selectedBox ?? default12TamponConfiguration);
  const [padsBoxState, setPadsBoxState] = useState<PadsBoxState>(defaultPadsConfiguration);
  const [padsBoxStackState, setPadsBoxStackState] = useState<PadsBoxStackState>(defaultPadsStackConfiguration);
  const [showQuantity, setShowQuantity] = useState(true);
  const [subscriptionId, setSubscriptionId] = useState('');
  const [configVariant, setConfigVariant] = useState('0');
  const [layout, setLayout] = useState('0');
  const [subscriptions, setSubscriptions] = useState<SubscriptionPlan[]>([]);
  const [isTracked, setIsTracked] = useState(false);
  const hasFeedbackView = feedback && feedback.length > 0 && feedback[0].feedback_text.text !== '';
  const topSubtitleText = topSubtitle ? replaceText(topSubtitle, country, location) : topSubtitle;
  const paymentOptionsHeader = payment_options_header?.text?.length ? payment_options_header.text : t('components.payment-options');
  const headingText = replaceText(heading, country, location);
  const bodyText = replaceText(text, country, location);
  const subtextText = replaceText(subtext, country, location);
  const [prices, setPrices] = useState<Prices>({});
  const fetch = useFetch();
  const isTamponBox = productCode === ProductCode.TamponBox;
  const isPads = productCode === ProductCode.Pads;
  const isVMS = productCode === ProductCode.ScreeningKit;
  const hasProductOptions =
    productHeaderData.productOptions &&
    productHeaderData.productOptions.length > 0 &&
    (productHeaderData.productOptions[0].one_off_sku?.text !== '' || productHeaderData.productOptions[0].subscription_sku?.text !== '');
  const { productOptionsInfo, productAddonsInfo } = useMemo(() => {
    if (hasProductOptions) {
      return productHeaderData.productOptions.reduce<{ productOptionsInfo: ProductOption[]; productAddonsInfo: ProductAddon[] }>(
        (acc, option) => {
          const formattedOption = {
            oneOffSku: option.one_off_sku.text !== '' ? option.one_off_sku.text : null,
            subscriptionSku: option.subscription_sku.text !== '' ? option.subscription_sku.text : null,
            subscriptionPlan: option.subscription_plan.text !== '' ? option.subscription_plan.text : null,
            label: option.label.text,
            title: option.title?.text ?? '',
            includes: option.subscription_includes?.text ?? '',
            optionInfo: option.option_info_json?.text ?? ''
          };

          if (option.addon) {
            acc.productAddonsInfo.push(formattedOption);
          } else {
            acc.productOptionsInfo.push(formattedOption);
          }

          return acc;
        },
        { productOptionsInfo: [], productAddonsInfo: [] }
      );
    }

    return { productOptionsInfo: [], productAddonsInfo: [] };
  }, [hasProductOptions, productHeaderData.productOptions]);
  const [scrollHeight, setScrollHeight] = useState<number>();

  const paymentOptions: {
    label: string;
    value: string;
  }[] = [];

  if (productHeaderData.options) {
    productHeaderData.options.map((option) => {
      paymentOptions.push({ label: option.label.text, value: option.value.text });
      return null;
    });
  }

  const [paymentOptionSelected, setPaymentOptionSelected] = useState(paymentOptions && paymentOptions[0]);
  const isDiagnosticTamponProduct = DIAGNOSTIC_TAMPON_PRODUCT_CODES.includes(productCode);
  const showNurseConsultation =
    isDiagnosticTamponProduct && (country === CountryCode.GB || (country === CountryCode.US && productCode === ProductCode.ScreeningKit));
  const [productId, setProductId] = useState<string>(`${productCode}${paymentOptionSelected ? paymentOptionSelected?.value : ''}`);

  useEffect(() => {
    (async () => {
      await getPrices();
    })();
  }, [currency, boxState, padsBoxState, padsBoxStackState]);

  useEffect(() => {
    if (price !== -1) {
      updatePlans();
    }
    if (price !== -1 && !isTracked && firebaseAuth) {
      handleTracking();
      setIsTracked(true);
    }
  }, [price, prices, firebaseAuth]);

  const handleTracking = async () => {
    if (!firebaseAuth) return;
    const { currentUser } = firebaseAuth;
    const userEmail = currentUser?.email ?? '';

    let productVariant;
    if (isTamponBox && country !== CountryCode.US) {
      // TODO: review how to handle US tampon box page view tracking
      productVariant = convertBoxStateToBoxItems(boxState);
    }
    productPageTrack(productId, userEmail, price, productVariant);
  };

  const getPrices = async () => {
    try {
      let productPrice: number | null;

      const linerPadPrice = await getLinerPadsPrice(country, currency);
      const regularPadPrice = await getRegularPadsPrice(country, currency);
      const nightPadPrice = await getSuperPadsPrice(country, currency);
      const nightPlusPadPrice = await getSuperPlusPadsPrice(country, currency);

      if (isTamponBox) {
        productPrice = await getBoxPrice(boxState as Variant, currency, country);
        setPrice(productPrice || 0);
      } else if (isPads) {
        productPrice = await getPadsPrice(padsBoxStackState, padsBoxState, currency, country);

        setPrice(productPrice);
      } else if (isVMS) {
        const newProductPrice = await getProductPrice(ProductCode.VMSEssential, currency, country);
        setPrice(newProductPrice?.price || 0);
      } else {
        const newProductPrice = await getProductPrice(productId, currency, country);
        setPrice(newProductPrice?.price || 0);
      }
      const { pathname } = location;
      if (pathname.includes('sampler-initiation')) {
        await handleSamplerIntiation();
      }
      const cbdTamponPrice = (await getBoxPrice({ cbdregular: 1 }, currency, country)) || 0;
      const nakedTamponPrice = (await getBoxPrice({ nakedregular: 1 }, currency, country)) || 0;
      const pouchPrice = (await getProductPrice(ProductCode.TamponPouch, currency, country))?.price || 0;
      const tinPrice = (await getProductPrice(ProductCode.TamponTin, currency, country))?.price || 0;

      setPrices((currentPrices) => ({
        ...currentPrices,
        cbdTamponPrice,
        nakedTamponPrice,
        pouchPrice,
        tinPrice,
        regularPadPrice,
        nightPadPrice,
        nightPlusPadPrice,
        linerPadPrice
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const handleSamplerIntiation = async () => {
    if (!firebaseAuth) return;
    const { currentUser } = firebaseAuth;
    const orders = currentUser ? await getAccountOrders() : false;
    if (!orders || orders.length === 0) {
      setShowQuantity(false);
      setPrice(0);
      setProductId(`${productCode}_initiation`);
    }
  };

  const getAccountOrders = async () => {
    const url = `/customers/orders/`;
    const orders = await fetch.get(url);
    return orders;
  };

  const updatePlans = async () => {
    const updatedPlans = await handleFetchProductPriceAndSubscriptionPlans(price, productId, country);
    if (!updatedPlans) return;
    if (configVariant === '' || configVariant === '0') {
      const filteredPlans = updatedPlans.filter((plan) => plan.planCode !== SubscriptionPlanCode.Tampons6MonthUpfront);
      setSubscriptions(filteredPlans);
      setLayout('0');
    }
    if (configVariant === '1') {
      const filteredPlans = updatedPlans.filter(
        (plan) =>
          plan.planCode === SubscriptionPlanCode.Tampons1Month ||
          plan.planCode === SubscriptionPlanCode.Tampons3Month ||
          plan.planCode === SubscriptionPlanCode.Tampons6MonthUpfront
      );
      setSubscriptions(filteredPlans);
      setLayout('0');
    }
    if (configVariant === '2') {
      const filteredPlans = updatedPlans.filter(
        (plan) => plan.planCode !== SubscriptionPlanCode.Tampons6MonthUpfront && plan.planCode !== SubscriptionPlanCode.Tampons12MonthUpfront
      );
      setSubscriptions(filteredPlans);
      setLayout('1');
    }
    setLoaded(true);
  };

  return (
    <ShoppingContext.Provider
      value={{
        selectedBox,
        productHeaderData,
        filteredLinks,
        hasFeedbackView,
        topSubtitleText,
        paymentOptionsHeader,
        headingText,
        bodyText,
        subtextText,
        orderedSlides,
        setLoaded,
        setPrice,
        setBoxState,
        setPadsBoxState,
        setPadsBoxStackState,
        setShowQuantity,
        setSubscriptionId,
        setConfigVariant,
        setLayout,
        setSubscriptions,
        setIsTracked,
        setPrices,
        paymentOptions,
        setPaymentOptionSelected,
        isUsingNewDesignSystem: !!is_using_new_design_system,
        isDiagnosticTamponProduct,
        showNurseConsultation,
        setProductId,
        setScrollHeight,
        loaded,
        showQuantity,
        subscriptionId,
        layout,
        subscriptions,
        scrollHeight,
        price,
        boxState,
        padsBoxState,
        padsBoxStackState,
        configVariant,
        isTracked,
        prices,
        paymentOptionSelected,
        productId,
        preselectedTamponBoxes,
        hasProductOptions,
        productAddonsInfo,
        selectedOptionCartItem,
        selectedAddonCartItems,
        selectedCartItems,
        setSelectedOptionCartItem,
        setSelectedAddonCartItems,
        productOptionsInfo
      }}
    >
      {children}
    </ShoppingContext.Provider>
  );
};
